/**
 * Utility functions for adjusting dates when duplicating events
 */

import { parseDateOnly } from './dateUtils';

/**
 * Calculate the time difference in milliseconds between two date-only strings.
 */
export const getTimeDifference = (oldDate: string, newDate: string): number => {
  return parseDateOnly(newDate).getTime() - parseDateOnly(oldDate).getTime();
};

/**
 * Adjust a timestamp based on the difference between old and new start dates
 * @param timestamp ISO timestamp to adjust
 * @param oldStartDate Original event start date (YYYY-MM-DD)
 * @param newStartDate New event start date (YYYY-MM-DD)
 * @returns Adjusted ISO timestamp
 */
export const adjustTimestamp = (
  timestamp: string,
  oldStartDate: string,
  newStartDate: string
): string => {
  const timeDiff = getTimeDifference(oldStartDate, newStartDate);
  const originalDate = new Date(timestamp);
  const adjustedDate = new Date(originalDate.getTime() + timeDiff);
  return adjustedDate.toISOString();
};

/**
 * Adjust UTC timestamp string (used for radar periods)
 * @param utcTimestamp UTC timestamp string (e.g., "2025-11-06 13:00:00.000000 UTC")
 * @param oldStartDate Original event start date (YYYY-MM-DD)
 * @param newStartDate New event start date (YYYY-MM-DD)
 * @returns Adjusted UTC timestamp string
 */
export const adjustUTCTimestamp = (
  utcTimestamp: string,
  oldStartDate: string,
  newStartDate: string
): string => {
  try {
    // Remove ' UTC' suffix if present and parse
    const cleanTimestamp = utcTimestamp.replace(' UTC', '').trim();
    const timeDiff = getTimeDifference(oldStartDate, newStartDate);
    
    // Parse the timestamp: "2025-11-06 13:00:00.000000" -> replace space with T and add Z
    const isoFormat = cleanTimestamp.replace(' ', 'T') + 'Z';
    const originalDate = new Date(isoFormat);
    
    // Validate the date
    if (isNaN(originalDate.getTime())) {
      console.error('Invalid UTC timestamp:', utcTimestamp);
      return utcTimestamp; // Return original if parsing fails
    }
    
    const adjustedDate = new Date(originalDate.getTime() + timeDiff);
    
    // Format back to the original format: "YYYY-MM-DD HH:mm:ss.SSSSSS UTC"
    const isoString = adjustedDate.toISOString();
    const [datePart, timePart] = isoString.split('T');
    const timeWithoutZ = timePart.replace('Z', '');
    
    return `${datePart} ${timeWithoutZ} UTC`;
  } catch (error) {
    console.error('Error adjusting UTC timestamp:', error, utcTimestamp);
    return utcTimestamp; // Return original if any error occurs
  }
};

/**
 * Calculate the number of days between two dates
 */
export const getDaysDifference = (date1: string, date2: string): number => {
  const oneDay = 24 * 60 * 60 * 1000;
  return Math.round(Math.abs((parseDateOnly(date1).getTime() - parseDateOnly(date2).getTime()) / oneDay));
};

/**
 * Format date for display (e.g., "Nov 6, 2025")
 */
export const formatDateDisplay = (dateString: string): string => {
  const date = parseDateOnly(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

